/**
 * Plugin System
 *
 * Plugins provide nodes and optionally custom endpoint types.
 * PluginRegistry delegates node and endpoint creation to the appropriate plugin.
 */

import type { NodeDef } from '@runtime/IRuntime';
import type { IEndpoint } from '@/runtime/IEndpoint';
import type { Endpoint } from '@/nodes/Endpoint';
import type { IRuntimeStore } from '@runtime/RuntimeStore';
import type { INode } from '@nodes/INode';
import type { BaseDependencies, RepeaterDomainAdapter } from '@nodes/Dependencies';
import type { EndpointsRecord } from '@nodes/TypedEndpoints';
import type { Domain } from '@nodes/Endpoint';
import type { GraphIndex } from '@/runtime/GraphIndex';


/**
 * Dependencies passed to plugin.createEndpoint().
 * Endpoints derive storeKey as: `${instancePath}.${node.id}.${endpoint.id}`
 */
export interface EndpointWithContext {
  instancePath: string;  // Full path of containing instance (e.g., 'root/mysynth1')
  node: INode;           // Node reference - use node.id for nodeId
  endpoint: Endpoint;
  graphIndex: GraphIndex;
}

// ─────────────────────────────────────────────────────────────
// Plugin Interface
// ─────────────────────────────────────────────────────────────

/**
 * Plugin interface for domain-specific extensions.
 * Plugins provide nodes and optionally custom endpoint types.
 */
export interface Plugin {
  /** Unique plugin identifier */
  id: string;

  /** Plugin display name */
  name: string;

  /**
   * Namespace prefix for node types (e.g., 'elem', 'tone').
   * When set, nodes are registered as 'namespace.type' (e.g., 'elem.Synth').
   * Core plugins typically don't use a namespace.
   */
  namespace?: string;

  /**
   * Default domain for all nodes in this plugin (e.g., Domain.Video).
   * Applied to nodes that don't specify their own domain.
   */
  domain?: Domain;

  /** Node definitions provided by this plugin (for metadata/endpoints lookup) */
  nodes?: NodeDef[];

  /** Endpoint data types this plugin handles (e.g., ['audio']) */
  endpointDataTypes?: string[];

  /**
   * Runtime store reference, set by PluginRegistry during registration.
   * Available for plugins that create PropagatingEndpoints.
   */
  store?: IRuntimeStore;

  /**
   * Create a node instance.
   * @param type - Unqualified type (e.g., 'Synth', not 'elem.Synth')
   * @param id - Node instance id
   * @param deps - Base dependencies from Runtime (transport, endpointApi)
   */
  createNode: (type: string, id: string, deps: BaseDependencies) => INode;

  /**
   * Create an endpoint instance for a custom endpoint type.
   * Only called for types listed in endpointDataTypes.
   * Use this.store for PropagatingEndpoint construction.
   */
  createEndpoint?: (config: EndpointWithContext) => IEndpoint;

  /** Called when plugin is registered */
  onRegister?: () => void | Promise<void>;

  /** Domain adapters for template-based Repeater (registered after onRegister) */
  getRepeaterAdapters?: () => RepeaterDomainAdapter[];

  /** Called when plugin is unregistered */
  onUnregister?: () => void;

  /**
   * Register UI components (visualizations, etc.)
   * Called by UI layer at app init, after runtime is ready.
   * Uses dynamic imports to avoid circular dependencies.
   * @param context - Provides typed helpers like registerViz
   */
  registerUI?: (context: import('@/ui/components/node/VisualizationRegistry').RegisterUIContext) => Promise<void>;
}

// ─────────────────────────────────────────────────────────────
// Plugin Registry
// ─────────────────────────────────────────────────────────────

/**
 * Registry for plugins.
 * Delegates node and endpoint creation to the appropriate plugin.
 */
export class PluginRegistry {
  private plugins: Map<string, Plugin> = new Map();

  // Maps qualified node type -> plugin that owns it
  private nodeOwners: Map<string, Plugin> = new Map();

  // Maps endpoint type -> plugin that handles it
  private endpointOwners: Map<string, Plugin> = new Map();

  // Node definitions for metadata lookup (endpoints, presets, etc.)
  private nodeDefs: Map<string, NodeDef> = new Map();

  private store: IRuntimeStore;

  constructor(store: IRuntimeStore) {
    this.store = store;
  }

  /**
   * Register a plugin.
   */
  registerPlugin(plugin: Plugin): void {
    if (this.plugins.has(plugin.id)) {
      throw new Error(`Plugin '${plugin.id}' is already registered`);
    }

    // Register node types
    if (plugin.nodes) {
      for (const nodeDef of plugin.nodes) {
        const qualifiedType = plugin.namespace
          ? `${plugin.namespace}.${nodeDef.type}`
          : nodeDef.type;

        if (this.nodeOwners.has(qualifiedType)) {
          throw new Error(`Node type '${qualifiedType}' is already registered`);
        }

        this.nodeOwners.set(qualifiedType, plugin);

        // Store qualified def for metadata lookup
        // Apply plugin's domain if node doesn't have one
        const qualifiedDef = {
          ...nodeDef,
          type: qualifiedType,
          domain: nodeDef.domain ?? plugin.domain
        };
        this.nodeDefs.set(qualifiedType, qualifiedDef);
      }
    }

    // Register endpoint types and provide store
    if (plugin.endpointDataTypes) {
      plugin.store = this.store;
      for (const type of plugin.endpointDataTypes) {
        if (this.endpointOwners.has(type)) {
          throw new Error(`Endpoint type '${type}' is already registered`);
        }
        this.endpointOwners.set(type, plugin);
      }
    }

    this.plugins.set(plugin.id, plugin);
    plugin.onRegister?.();
  }

  /**
   * Unregister a plugin.
   */
  unregisterPlugin(pluginId: string): void {
    const plugin = this.plugins.get(pluginId);
    if (!plugin) return;

    plugin.onUnregister?.();

    // Unregister node types
    if (plugin.nodes) {
      for (const nodeDef of plugin.nodes) {
        const qualifiedType = plugin.namespace
          ? `${plugin.namespace}.${nodeDef.type}`
          : nodeDef.type;
        this.nodeOwners.delete(qualifiedType);
        this.nodeDefs.delete(qualifiedType);
      }
    }

    // Unregister endpoint types
    if (plugin.endpointDataTypes) {
      for (const type of plugin.endpointDataTypes) {
        this.endpointOwners.delete(type);
      }
    }

    this.plugins.delete(pluginId);
  }

  // ─────────────────────────────────────────────────────────────
  // Node operations
  // ─────────────────────────────────────────────────────────────

  /**
   * Check if a node type is registered.
   */
  hasNode(type: string): boolean {
    return this.nodeOwners.has(type);
  }

  /**
   * Get endpoints for a node type.
   */
  getEndpoints(type: string): EndpointsRecord | undefined {
    return this.nodeDefs.get(type)?.endpoints;
  }

  /**
   * Get a node definition by type.
   */
  getNodeDef(type: string): NodeDef | undefined {
    return this.nodeDefs.get(type);
  }

  /**
   * Get all registered node definitions (for UI menus, etc.)
   */
  getAllNodeDefs(): NodeDef[] {
    return Array.from(this.nodeDefs.values());
  }

  /**
   * Create a node instance.
   * Delegates to the plugin that owns this node type.
   */
  createNode(qualifiedType: string, id: string, deps: BaseDependencies): INode {
    const plugin = this.nodeOwners.get(qualifiedType);
    if (!plugin) {
      throw new Error(`Unknown node type: ${qualifiedType}`);
    }

    // Extract unqualified type for plugin
    const unqualifiedType = plugin.namespace
      ? qualifiedType.slice(plugin.namespace.length + 1)
      : qualifiedType;

    return plugin.createNode(unqualifiedType, id, deps);
  }

  /**
   * Get a factory preset's values for a node type.
   * Returns the preset params object, or undefined if not found.
   */
  getPreset(qualifiedType: string, presetName: string): Record<string, any> | undefined {
    const nodeDef = this.nodeDefs.get(qualifiedType);
    if (!nodeDef?.factoryPresets) return undefined;

    const preset = nodeDef.factoryPresets.find(p => p.name === presetName);
    return preset?.params;
  }

  // ─────────────────────────────────────────────────────────────
  // Endpoint operations
  // ─────────────────────────────────────────────────────────────

  /**
   * Resolve the plugin that owns an endpoint dataType.
   * For union types like 'array|polylines', uses the first constituent type.
   */
  private resolveEndpointPlugin(dataType: string): Plugin | undefined {
    if (!dataType) return undefined;
    const plugin = this.endpointOwners.get(dataType);
    if (plugin) return plugin;
    // Union type: try first constituent
    if (dataType.includes('|')) {
      return this.endpointOwners.get(dataType.split('|')[0]);
    }
    return undefined;
  }

  /**
   * Check if an endpoint type has a custom handler.
   */
  hasEndpointType(type: string): boolean {
    return this.resolveEndpointPlugin(type) !== undefined;
  }

  /**
   * Create an endpoint instance.
   * Delegates to the plugin that handles this endpoint type.
   */
  createEndpoint(config: EndpointWithContext): IEndpoint {
    const plugin = this.resolveEndpointPlugin(config.endpoint.dataType);
    if (!plugin?.createEndpoint) {
      throw new Error(`No plugin handles endpoint type '${config.endpoint.dataType}'`);
    }
    return plugin.createEndpoint(config);
  }

  // ─────────────────────────────────────────────────────────────
  // Plugin access
  // ─────────────────────────────────────────────────────────────

  getPlugin(pluginId: string): Plugin | undefined {
    return this.plugins.get(pluginId);
  }

  getAllPlugins(): Plugin[] {
    return Array.from(this.plugins.values());
  }
}
